/**
 * Native {@link SessionRecordingUploader}: the persisted, resumable S3
 * multipart upload queue for Premium Session Recording.
 *
 * Per recording: join the local 60 s segments into one `.m4a` (native,
 * passthrough, idempotent) → `POST …/upload` (create / resume: presigned
 * UploadPart URLs for the missing parts + ETags of the parts S3 already has) →
 * native `uploadPart` per missing part (byte range streamed from disk; the
 * audio never enters JS) → `POST …/complete` → delete the local audio → `done`.
 * Presigned URLs are refreshed by calling `upload` again when they are about to
 * expire or a part PUT is refused (403 / 404 / 400 from S3).
 *
 * Survives restarts: the queue is persisted per user (content-free: ids,
 * duration, local path, size, part ETags, attempt, phase) and reloaded on
 * start; the server reports which parts are already uploaded.
 *
 * Retry policy mirrors the voice-note uploader: backoff `15 s · 2^(n−1)` capped
 * at 10 min (±20 % jitter), kicked by connectivity changes (`@capacitor/network`,
 * browser `online`), visibility, a 45 s interval and new work. Permanent 4xx
 * (other than 401 / 408 / 429) park the item as `failed` with the server's
 * user-safe message; 410, or 404 with the server's not-found code, means the
 * recording is gone server-side — the item is dropped and the local audio
 * deleted (a bare 404 is retried). "Upload on Wi-Fi only" (per
 * device, default off) holds items in `waiting_for_wifi`; offline items wait in
 * `waiting_for_network` (neither counts as an attempt).
 *
 * Nothing here logs ids, paths or content.
 */

import {
  SESSION_RECORDING_CONTENT_TYPE,
  SESSION_RECORDING_ERROR_CODES,
  SESSION_RECORDING_MAX_BYTES,
  type CompleteSessionRecordingUploadRequest,
  type SessionRecordingStatus,
  type SessionRecordingUploadPlan,
  type StartSessionRecordingUploadRequest,
} from '@shared/sessions/session-recording'
import type {
  SessionRecordingUploadPhase,
  SessionRecordingUploadState,
  SessionRecordingUploader,
} from './uploader-port'

export const SESSION_RECORDING_WIFI_ONLY_KEY = 'ascend-session-recording-wifi-only'

const RETRY_BASE_MS = 15_000
const RETRY_CAP_MS = 10 * 60_000
const DRAIN_INTERVAL_MS = 45_000
/** Refresh the presigned URLs when they expire within this margin. */
const URL_EXPIRY_MARGIN_MS = 60_000
/** PUT attempts per part within one pass (then the item backs off). */
const PART_ATTEMPTS = 3
const PART_RETRY_DELAYS_MS = [2_000, 5_000]
/** A 400 from `complete` (ETag mismatch) is resumed this many times before it is final. */
const COMPLETE_MISMATCH_RETRIES = 2

/** Server statuses meaning the server already holds the uploaded audio. */
const SERVER_HAS_AUDIO: ReadonlySet<SessionRecordingStatus> = new Set([
  'uploaded',
  'transcribing',
  'drafting',
  'draft_ready',
  'approved',
])

const MESSAGES = {
  offline: 'No connection — we’ll keep trying.',
  retry: 'Upload interrupted — we’ll try again shortly.',
  missingLocal: 'This recording is no longer on this phone.',
  tooLarge: 'This recording is too large to upload.',
  signedOut: 'Sign in again to upload this recording.',
  serverFailed: 'The server stopped this upload. Retry, or discard the recording.',
} as const

/** Compute the next retry delay (exported for tests). */
export function sessionRecordingRetryDelayMs(attempt: number, random: () => number = Math.random): number {
  const base = Math.min(RETRY_BASE_MS * 2 ** Math.max(0, attempt - 1), RETRY_CAP_MS)
  return Math.round(base * (0.8 + random() * 0.4))
}

export interface UploaderNetworkStatus {
  connected: boolean
  connectionType: 'wifi' | 'cellular' | 'none' | 'unknown'
}

export interface UploaderKeyValueStore {
  get(options: { key: string }): Promise<{ value: string | null }>
  set(options: { key: string; value: string }): Promise<void>
  remove(options: { key: string }): Promise<void>
}

export interface NativeSessionRecordingUploaderDeps {
  storage: UploaderKeyValueStore
  /** Key prefix for the per-user queue (`<prefix><encoded uid>`). */
  queueKeyPrefix: string
  /** Signed-in user id, null when signed out. Throws on a transient failure. */
  getUserId: () => Promise<string | null>
  startUpload: (
    sessionUuid: string,
    recordingId: string,
    body: StartSessionRecordingUploadRequest,
  ) => Promise<SessionRecordingUploadPlan>
  completeUpload: (
    sessionUuid: string,
    recordingId: string,
    body: CompleteSessionRecordingUploadRequest,
  ) => Promise<unknown>
  /** Server status for a 409 (null when the recording is not listed). */
  getRecordingStatus: (sessionUuid: string, recordingId: string) => Promise<SessionRecordingStatus | null>
  /** Native passthrough join (idempotent). Rejects with code `NOT_FOUND` when the audio is gone. */
  join: (recordingId: string) => Promise<{ path: string; bytes: number }>
  uploadPart: (options: {
    path: string
    offset: number
    length: number
    url: string
    allowCellular: boolean
  }) => Promise<{ status: number; etag: string | null }>
  /** Delete the local audio (the recorder's `discardLocal`). */
  discardLocal: (recordingId: string) => Promise<void>
  getNetworkStatus: () => Promise<UploaderNetworkStatus>
  /** Subscribe to connectivity changes; returns an unsubscribe. */
  onNetworkChange?: (listener: (status: UploaderNetworkStatus) => void) => () => void
  now?: () => number
  random?: () => number
  sleep?: (ms: number) => Promise<void>
}

export interface NativeSessionRecordingUploader extends SessionRecordingUploader {
  readonly kind: 'native'
  /** Load the queue + settings and attach the retry kicks (idempotent). */
  start(): Promise<void>
  /** Resolves once the persisted queue for the current user is loaded. */
  whenReady(): Promise<void>
  /** True when the queue holds (or just finished) this recording. */
  isTracked(recordingId: string): Promise<boolean>
  /** Run one drain pass now (shares an in-flight pass). */
  drain(): Promise<void>
  /** Sign-out: forget every item in memory (storage + audio are wiped by the caller). */
  resetForSignOut(): void
  dispose(): void
}

interface QueueItem {
  recordingId: string
  sessionUuid: string
  durationSec: number
  /** Joined `.m4a` on the device (after the first join). */
  path: string | null
  sizeBytes: number | null
  partSizeBytes: number | null
  /** partNumber → ETag for parts S3 has. */
  etags: Record<string, string>
  attempt: number
  phase: SessionRecordingUploadPhase
  error: string | null
  nextRetryAt: number | null
  createdAt: number
}

interface PersistedQueue {
  v: 1
  uid: string
  items: QueueItem[]
}

type Outcome =
  | { kind: 'retry'; message: string }
  | { kind: 'failed'; message: string }
  | { kind: 'gone' }
  | { kind: 'serverHasAudio' }

class OutcomeError extends Error {
  constructor(readonly outcome: Outcome) {
    super(outcome.kind)
    this.name = 'SessionRecordingUploadOutcome'
  }
}

/**
 * Server error codes that mean the recording (or its session / audio) no longer
 * exists. A 404 WITHOUT one of these (e.g. a route not deployed yet) is retried,
 * never treated as "gone" — that would delete the only copy of the audio.
 */
const GONE_CODES: ReadonlySet<string> = new Set([
  SESSION_RECORDING_ERROR_CODES.notFound,
  SESSION_RECORDING_ERROR_CODES.sessionNotFound,
  SESSION_RECORDING_ERROR_CODES.audioGone,
])

/** HTTP-shaped error from the sessions API client (`SessionRecordingApiError`). */
function apiStatus(err: unknown): number | null {
  if (!(err instanceof Error) || err.name !== 'SessionRecordingApiError') return null
  const status = (err as { status?: unknown }).status
  return typeof status === 'number' ? status : null
}

function apiCode(err: unknown): string | null {
  const code = (err as { code?: unknown } | null)?.code
  return typeof code === 'string' ? code : null
}

function nativeCode(err: unknown): string | null {
  const code = (err as { code?: unknown } | null)?.code
  return typeof code === 'string' ? code : null
}

function isPersistablePhase(phase: SessionRecordingUploadPhase): boolean {
  return phase !== 'done'
}

function sanitizeItem(raw: unknown): QueueItem | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Partial<QueueItem>
  if (typeof r.recordingId !== 'string' || typeof r.sessionUuid !== 'string') return null
  const etags: Record<string, string> = {}
  if (r.etags && typeof r.etags === 'object') {
    for (const [k, v] of Object.entries(r.etags)) if (typeof v === 'string' && v) etags[k] = v
  }
  // Anything mid-flight when the app died starts again from `queued`.
  const phase: SessionRecordingUploadPhase = r.phase === 'failed' ? 'failed' : 'queued'
  return {
    recordingId: r.recordingId,
    sessionUuid: r.sessionUuid,
    durationSec: typeof r.durationSec === 'number' && r.durationSec >= 0 ? r.durationSec : 0,
    path: typeof r.path === 'string' ? r.path : null,
    sizeBytes: typeof r.sizeBytes === 'number' ? r.sizeBytes : null,
    partSizeBytes: typeof r.partSizeBytes === 'number' ? r.partSizeBytes : null,
    etags,
    attempt: typeof r.attempt === 'number' ? r.attempt : 0,
    phase,
    error: phase === 'failed' && typeof r.error === 'string' ? r.error : null,
    nextRetryAt: phase === 'queued' && typeof r.nextRetryAt === 'number' ? r.nextRetryAt : null,
    createdAt: typeof r.createdAt === 'number' ? r.createdAt : 0,
  }
}

export function createNativeSessionRecordingUploader(
  deps: NativeSessionRecordingUploaderDeps,
): NativeSessionRecordingUploader {
  const now = deps.now ?? (() => Date.now())
  const random = deps.random ?? Math.random
  const sleep = deps.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)))

  const items = new Map<string, QueueItem>()
  const done = new Map<string, QueueItem>()
  const listeners = new Set<() => void>()
  let uid: string | null = null
  let wifiOnly = false
  /** Bumped by sign-out / account switch: an in-flight pass for the old queue stops writing. */
  let generation = 0
  let readyPromise: Promise<void> | null = null
  /** Per-device, so it survives sign-out (unlike readyPromise). */
  let settingsPromise: Promise<void> | null = null
  let started = false
  let drainPromise: Promise<void> | null = null
  let rerun = false
  let retryTimer: ReturnType<typeof setTimeout> | null = null
  const teardown: Array<() => void> = []
  let persistChain: Promise<void> = Promise.resolve()

  let stateCache = new Map<string, SessionRecordingUploadState>()
  let listCache: SessionRecordingUploadState[] = []

  const queueKey = (userId: string) => `${deps.queueKeyPrefix}${encodeURIComponent(userId)}`

  function partLength(item: QueueItem, partNumber: number): number {
    if (item.sizeBytes == null || item.partSizeBytes == null) return 0
    const offset = (partNumber - 1) * item.partSizeBytes
    return Math.max(0, Math.min(item.partSizeBytes, item.sizeBytes - offset))
  }

  function progressOf(item: QueueItem): number {
    if (item.phase === 'done') return 1
    if (!item.sizeBytes) return 0
    let uploaded = 0
    for (const key of Object.keys(item.etags)) uploaded += partLength(item, Number(key))
    return Math.min(1, uploaded / item.sizeBytes)
  }

  function toState(item: QueueItem): SessionRecordingUploadState {
    return {
      recordingId: item.recordingId,
      sessionUuid: item.sessionUuid,
      phase: item.phase,
      progress: progressOf(item),
      bytesTotal: item.sizeBytes,
      error: item.error,
      attempt: item.attempt,
      nextRetryAt: item.nextRetryAt,
    }
  }

  function notify(): void {
    const nextCache = new Map<string, SessionRecordingUploadState>()
    const all = [...items.values(), ...done.values()].sort((a, b) => a.createdAt - b.createdAt)
    let changed = all.length !== listCache.length
    for (const item of all) {
      const fresh = toState(item)
      const prev = stateCache.get(item.recordingId)
      const same =
        prev &&
        prev.phase === fresh.phase &&
        prev.progress === fresh.progress &&
        prev.bytesTotal === fresh.bytesTotal &&
        prev.error === fresh.error &&
        prev.attempt === fresh.attempt &&
        prev.nextRetryAt === fresh.nextRetryAt &&
        prev.sessionUuid === fresh.sessionUuid
      nextCache.set(item.recordingId, same ? prev : fresh)
      if (!same) changed = true
    }
    if (!changed) return
    stateCache = nextCache
    listCache = all.map((item) => nextCache.get(item.recordingId) as SessionRecordingUploadState)
    for (const listener of listeners) {
      try {
        listener()
      } catch {
        /* never break the queue on a subscriber bug */
      }
    }
  }

  function persist(): Promise<void> {
    const userId = uid
    const gen = generation
    if (!userId) return Promise.resolve()
    const snapshot: PersistedQueue = {
      v: 1,
      uid: userId,
      items: [...items.values()].filter((i) => isPersistablePhase(i.phase)).map((i) => ({ ...i })),
    }
    persistChain = persistChain
      .then(async () => {
        if (gen !== generation) return
        if (snapshot.items.length === 0) await deps.storage.remove({ key: queueKey(userId) })
        else await deps.storage.set({ key: queueKey(userId), value: JSON.stringify(snapshot) })
      })
      .catch(() => undefined)
    return persistChain
  }

  async function loadQueueFor(userId: string | null): Promise<void> {
    generation += 1
    items.clear()
    done.clear()
    uid = userId
    if (userId) {
      try {
        const { value } = await deps.storage.get({ key: queueKey(userId) })
        const parsed = value ? (JSON.parse(value) as Partial<PersistedQueue>) : null
        if (parsed && parsed.v === 1 && parsed.uid === userId && Array.isArray(parsed.items)) {
          for (const raw of parsed.items) {
            const item = sanitizeItem(raw)
            if (item) items.set(item.recordingId, item)
          }
        }
      } catch {
        /* unreadable queue → start empty; local audio is still found as orphans */
      }
    }
    notify()
  }

  async function loadSettings(): Promise<void> {
    try {
      const { value } = await deps.storage.get({ key: SESSION_RECORDING_WIFI_ONLY_KEY })
      wifiOnly = value === '1'
    } catch {
      wifiOnly = false
    }
  }

  /** Re-resolve the signed-in user; reload the queue on an account switch. */
  async function syncUser(): Promise<void> {
    let next: string | null
    try {
      next = await deps.getUserId()
    } catch {
      return // transient — keep the current queue
    }
    if (next !== uid) await loadQueueFor(next)
  }

  function settingsReady(): Promise<void> {
    if (!settingsPromise) settingsPromise = loadSettings()
    return settingsPromise
  }

  function whenReady(): Promise<void> {
    if (!readyPromise) {
      readyPromise = (async () => {
        await settingsReady()
        let userId: string | null = null
        try {
          userId = await deps.getUserId()
        } catch {
          userId = null
        }
        await loadQueueFor(userId)
      })()
    }
    return readyPromise
  }

  function isLive(item: QueueItem, gen: number): boolean {
    return gen === generation && items.get(item.recordingId) === item
  }

  function update(item: QueueItem, gen: number, patch: Partial<QueueItem>, save = true): void {
    if (!isLive(item, gen)) return
    Object.assign(item, patch)
    notify()
    if (save) void persist()
  }

  async function connectionGate(): Promise<SessionRecordingUploadPhase | null> {
    let status: UploaderNetworkStatus
    try {
      status = await deps.getNetworkStatus()
    } catch {
      return null // unknown → try; the request itself will tell
    }
    if (!status.connected || status.connectionType === 'none') return 'waiting_for_network'
    if (wifiOnly && status.connectionType !== 'wifi') return 'waiting_for_wifi'
    return null
  }

  /** Classify an API failure (create/resume or complete). */
  async function apiOutcome(item: QueueItem, err: unknown): Promise<Outcome> {
    const status = apiStatus(err)
    const message = err instanceof Error && err.message ? err.message : MESSAGES.retry
    if (status == null) return { kind: 'retry', message: MESSAGES.retry }
    if (status === 0) return { kind: 'retry', message: MESSAGES.offline }
    if (status === 410) return { kind: 'gone' }
    if (status === 404) {
      const code = apiCode(err)
      return code && GONE_CODES.has(code) ? { kind: 'gone' } : { kind: 'retry', message: MESSAGES.retry }
    }
    if (status === 401 || status === 408 || status === 429 || status >= 500) {
      return { kind: 'retry', message: MESSAGES.retry }
    }
    if (status === 409) {
      let serverStatus: SessionRecordingStatus | null
      try {
        serverStatus = await deps.getRecordingStatus(item.sessionUuid, item.recordingId)
      } catch {
        return { kind: 'retry', message: MESSAGES.retry }
      }
      // A conflict means the server knows the recording: a listing without it is
      // inconsistent, so keep the audio and retry rather than delete.
      if (serverStatus == null) return { kind: 'retry', message: MESSAGES.retry }
      if (serverStatus === 'withdrawn' || serverStatus === 'discarded') return { kind: 'gone' }
      if (SERVER_HAS_AUDIO.has(serverStatus)) return { kind: 'serverHasAudio' }
      if (serverStatus === 'failed') return { kind: 'failed', message: MESSAGES.serverFailed }
      return { kind: 'retry', message: MESSAGES.retry } // recording / uploading: stop event in flight
    }
    return { kind: 'failed', message }
  }

  async function requestPlan(item: QueueItem): Promise<SessionRecordingUploadPlan> {
    try {
      return await deps.startUpload(item.sessionUuid, item.recordingId, {
        contentType: SESSION_RECORDING_CONTENT_TYPE,
        sizeBytes: item.sizeBytes ?? 0,
        // The server requires ≥ 1 s (a sub-second capture still has a joined file).
        durationSec: Math.max(1, Math.round(item.durationSec)),
      })
    } catch (err) {
      throw new OutcomeError(await apiOutcome(item, err))
    }
  }

  /** Server is authoritative: adopt its ETags, forget ours for parts it still wants. */
  function adoptPlan(item: QueueItem, gen: number, plan: SessionRecordingUploadPlan): void {
    const etags = { ...item.etags }
    for (const part of plan.parts) {
      if (part.etag) etags[String(part.partNumber)] = part.etag
      else if (part.url) delete etags[String(part.partNumber)]
    }
    update(item, gen, { etags, partSizeBytes: plan.partSizeBytes })
  }

  function planExpiresSoon(plan: SessionRecordingUploadPlan): boolean {
    const at = Date.parse(plan.expiresAt)
    return Number.isFinite(at) && at - now() < URL_EXPIRY_MARGIN_MS
  }

  /** Upload one part with in-pass retries; refreshes the plan on expiry / refusal. */
  async function uploadOnePart(
    item: QueueItem,
    gen: number,
    partNumber: number,
    planRef: { plan: SessionRecordingUploadPlan },
  ): Promise<'uploaded' | 'paused'> {
    let refresh = planExpiresSoon(planRef.plan)
    for (let attempt = 1; ; attempt += 1) {
      if (!isLive(item, gen)) return 'paused'
      const gate = await connectionGate()
      if (gate) {
        update(item, gen, { phase: gate, error: null })
        return 'paused'
      }
      if (refresh) {
        planRef.plan = await requestPlan(item)
        if (!isLive(item, gen)) return 'paused'
        adoptPlan(item, gen, planRef.plan)
        refresh = false
      }
      if (item.etags[String(partNumber)]) return 'uploaded'
      const part = planRef.plan.parts.find((p) => p.partNumber === partNumber)
      const length = partLength(item, partNumber)
      if (!part?.url || length <= 0 || !item.path) {
        if (attempt >= PART_ATTEMPTS) throw new OutcomeError({ kind: 'retry', message: MESSAGES.retry })
        refresh = true
        continue
      }
      let result: { status: number; etag: string | null } | null = null
      let missingFile = false
      try {
        result = await deps.uploadPart({
          path: item.path,
          offset: (partNumber - 1) * (item.partSizeBytes ?? 0),
          length,
          url: part.url,
          allowCellular: !wifiOnly,
        })
      } catch (err) {
        missingFile = nativeCode(err) === 'NOT_FOUND'
      }
      if (missingFile) throw new OutcomeError({ kind: 'failed', message: MESSAGES.missingLocal })
      if (!isLive(item, gen)) return 'paused'
      if (result && result.status >= 200 && result.status < 300 && result.etag) {
        update(item, gen, {
          etags: { ...item.etags, [String(partNumber)]: result.etag },
          phase: 'uploading',
          error: null,
        })
        return 'uploaded'
      }
      // 403 = URL expired / signature; 404 = NoSuchUpload (aborted); 400 = RequestTimeout.
      if (result && (result.status === 403 || result.status === 404 || result.status === 400)) refresh = true
      if (attempt >= PART_ATTEMPTS) throw new OutcomeError({ kind: 'retry', message: MESSAGES.retry })
      await sleep(PART_RETRY_DELAYS_MS[attempt - 1] ?? PART_RETRY_DELAYS_MS[PART_RETRY_DELAYS_MS.length - 1])
    }
  }

  async function finishLocal(item: QueueItem, gen: number): Promise<void> {
    try {
      await deps.discardLocal(item.recordingId)
    } catch {
      /* the 7-day sweep / sign-out wipe is the backstop */
    }
    if (!isLive(item, gen)) return
    items.delete(item.recordingId)
    item.phase = 'done'
    item.error = null
    item.nextRetryAt = null
    done.set(item.recordingId, item)
    notify()
    void persist()
  }

  async function dropGone(item: QueueItem, gen: number): Promise<void> {
    try {
      await deps.discardLocal(item.recordingId)
    } catch {
      /* best-effort */
    }
    if (!isLive(item, gen)) return
    items.delete(item.recordingId)
    notify()
    void persist()
  }

  async function processItem(item: QueueItem, gen: number): Promise<void> {
    const gate = await connectionGate()
    if (gate) {
      update(item, gen, { phase: gate, error: null })
      return
    }
    update(item, gen, { phase: 'preparing', error: null, nextRetryAt: null }, false)

    let joined: { path: string; bytes: number }
    try {
      joined = await deps.join(item.recordingId)
    } catch (err) {
      if (nativeCode(err) === 'NOT_FOUND') {
        throw new OutcomeError({ kind: 'failed', message: MESSAGES.missingLocal })
      }
      throw new OutcomeError({ kind: 'retry', message: MESSAGES.retry })
    }
    if (!isLive(item, gen)) return
    if (!(joined.bytes > 0)) throw new OutcomeError({ kind: 'failed', message: MESSAGES.missingLocal })
    if (joined.bytes > SESSION_RECORDING_MAX_BYTES) {
      throw new OutcomeError({ kind: 'failed', message: MESSAGES.tooLarge })
    }
    update(item, gen, {
      path: joined.path,
      sizeBytes: joined.bytes,
      etags: item.sizeBytes != null && item.sizeBytes !== joined.bytes ? {} : item.etags,
    })

    const planRef = { plan: await requestPlan(item) }
    if (!isLive(item, gen)) return
    adoptPlan(item, gen, planRef.plan)
    update(item, gen, { phase: 'uploading' })

    const partNumbers = planRef.plan.parts.map((p) => p.partNumber).sort((a, b) => a - b)
    for (const partNumber of partNumbers) {
      if (item.etags[String(partNumber)]) continue
      const result = await uploadOnePart(item, gen, partNumber, planRef)
      if (result === 'paused') return
    }

    const missing = partNumbers.filter((n) => !item.etags[String(n)])
    if (missing.length > 0) throw new OutcomeError({ kind: 'retry', message: MESSAGES.retry })
    update(item, gen, { phase: 'completing' })
    try {
      await deps.completeUpload(item.sessionUuid, item.recordingId, {
        parts: partNumbers.map((partNumber) => ({ partNumber, etag: item.etags[String(partNumber)] })),
      })
    } catch (err) {
      if (apiStatus(err) === 400 && item.attempt < COMPLETE_MISMATCH_RETRIES) {
        // "Parts missing or don't match": forget ours; the next plan lists what S3 really has.
        update(item, gen, { etags: {} })
        throw new OutcomeError({ kind: 'retry', message: MESSAGES.retry })
      }
      throw new OutcomeError(await apiOutcome(item, err))
    }
    if (!isLive(item, gen)) return
    await finishLocal(item, gen)
  }

  async function runItem(item: QueueItem, gen: number): Promise<void> {
    try {
      await processItem(item, gen)
    } catch (err) {
      const outcome: Outcome =
        err instanceof OutcomeError ? err.outcome : { kind: 'retry', message: MESSAGES.retry }
      if (!isLive(item, gen)) return
      switch (outcome.kind) {
        case 'gone':
          await dropGone(item, gen)
          return
        case 'serverHasAudio':
          await finishLocal(item, gen)
          return
        case 'failed':
          update(item, gen, {
            phase: 'failed',
            error: outcome.message,
            attempt: item.attempt + 1,
            nextRetryAt: null,
          })
          return
        case 'retry': {
          const attempt = item.attempt + 1
          update(item, gen, {
            phase: 'queued',
            error: outcome.message,
            attempt,
            nextRetryAt: now() + sessionRecordingRetryDelayMs(attempt, random),
          })
        }
      }
    }
  }

  function dueItems(): QueueItem[] {
    const at = now()
    return [...items.values()]
      .filter(
        (i) =>
          (i.phase === 'queued' || i.phase === 'waiting_for_network' || i.phase === 'waiting_for_wifi') &&
          (i.nextRetryAt == null || i.nextRetryAt <= at),
      )
      .sort((a, b) => a.createdAt - b.createdAt)
  }

  function scheduleRetryTimer(): void {
    if (retryTimer) clearTimeout(retryTimer)
    retryTimer = null
    const next = [...items.values()]
      .filter((i) => i.phase === 'queued' && i.nextRetryAt != null)
      .reduce<number | null>((min, i) => (min == null || (i.nextRetryAt as number) < min ? i.nextRetryAt : min), null)
    if (next == null || !started) return
    retryTimer = setTimeout(() => void drain(), Math.max(1_000, next - now()))
  }

  async function drainLoop(): Promise<void> {
    await whenReady()
    await syncUser()
    do {
      rerun = false
      const gen = generation
      for (const item of dueItems()) {
        if (gen !== generation) break
        await runItem(item, gen)
      }
    } while (rerun)
    scheduleRetryTimer()
    await persistChain
  }

  function drain(): Promise<void> {
    if (drainPromise) {
      rerun = true
      return drainPromise
    }
    drainPromise = drainLoop().finally(() => {
      drainPromise = null
    })
    return drainPromise
  }

  const uploader: NativeSessionRecordingUploader = {
    kind: 'native',

    async enqueue({ recordingId, sessionUuid, durationSec }) {
      await whenReady()
      await syncUser()
      if (!uid) throw new Error(MESSAGES.signedOut)
      if (done.has(recordingId)) return
      const existing = items.get(recordingId)
      if (existing) {
        if (existing.phase === 'failed') {
          update(existing, generation, { phase: 'queued', error: null, nextRetryAt: null })
          void drain()
        }
        return
      }
      items.set(recordingId, {
        recordingId,
        sessionUuid,
        durationSec: Math.max(0, durationSec),
        path: null,
        sizeBytes: null,
        partSizeBytes: null,
        etags: {},
        attempt: 0,
        phase: 'queued',
        error: null,
        nextRetryAt: null,
        createdAt: now(),
      })
      notify()
      await persist()
      void drain()
    },

    retryNow(recordingId) {
      const item = items.get(recordingId)
      if (!item) return
      if (item.phase === 'failed' || item.phase.startsWith('waiting') || item.phase === 'queued') {
        update(item, generation, { phase: 'queued', nextRetryAt: null, error: null })
      }
      void drain()
    },

    async cancel(recordingId) {
      await whenReady()
      const removed = items.delete(recordingId) || done.delete(recordingId)
      if (!removed) return
      notify()
      await persist()
    },

    getState: (recordingId) => stateCache.get(recordingId) ?? null,

    list: () => listCache,

    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },

    getWifiOnly: () => wifiOnly,

    async loadWifiOnly() {
      await settingsReady()
      return wifiOnly
    },

    async setWifiOnly(on) {
      // A load still in flight must not overwrite the user's choice afterwards.
      await settingsReady()
      wifiOnly = on
      notify()
      try {
        await deps.storage.set({ key: SESSION_RECORDING_WIFI_ONLY_KEY, value: on ? '1' : '0' })
      } finally {
        // Re-evaluate waiting items either way (Wi-Fi only off releases them).
        for (const item of items.values()) {
          if (item.phase === 'waiting_for_wifi') update(item, generation, { phase: 'queued' }, false)
        }
        void drain()
      }
    },

    async start() {
      await whenReady()
      if (started) return
      started = true
      const kick = () => void drain()
      if (typeof window !== 'undefined') {
        window.addEventListener('online', kick)
        teardown.push(() => window.removeEventListener('online', kick))
      }
      if (typeof document !== 'undefined') {
        const onVisible = () => {
          if (document.visibilityState === 'visible') kick()
        }
        document.addEventListener('visibilitychange', onVisible)
        teardown.push(() => document.removeEventListener('visibilitychange', onVisible))
      }
      const interval = setInterval(kick, DRAIN_INTERVAL_MS)
      teardown.push(() => clearInterval(interval))
      if (deps.onNetworkChange) {
        teardown.push(
          deps.onNetworkChange((status) => {
            if (status.connected) kick()
          }),
        )
      }
      kick()
    },

    whenReady,

    async isTracked(recordingId) {
      await whenReady()
      await syncUser()
      return items.has(recordingId) || done.has(recordingId)
    },

    drain,

    resetForSignOut() {
      generation += 1
      items.clear()
      done.clear()
      uid = null
      readyPromise = Promise.resolve()
      if (retryTimer) clearTimeout(retryTimer)
      retryTimer = null
      notify()
    },

    dispose() {
      for (const off of teardown.splice(0)) off()
      if (retryTimer) clearTimeout(retryTimer)
      retryTimer = null
      started = false
      listeners.clear()
    },
  }

  return uploader
}
